import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useParams } from "react-router";
import TemplatesTable from "../components/Templates/TemplatesTable";
import DeleteTemplatesTable from "../components/Templates/DeleteTemplatesTable";
import { useState } from "react";
import { DeleteTemplateRequest } from "../models/Jobs/JobRequest";
import CreateExportTemplatesToCsvTable from "../components/Templates/CreateExportTemplatesToCsvTable";
import { Template } from "../models/Template";
import ExportTemplatesToCsvStatusTable from "../components/Templates/ExportTemplatesToCsvStatusTable";
import {UserRole} from "../models/User";
import { Settings2, X } from "lucide-react";
import { useManagementDrawer } from "../hooks/useManagementDrawer";

export enum ActiveTemplateJobsExplorer {
  DeleteTemplates = 0,
  ExportCsv = 1,
}

interface TemplatesPageProps {
  userRole: UserRole;
  isAssignedUser: boolean;
}

const TemplatesPage: React.FC<TemplatesPageProps> = ({
  userRole,
  isAssignedUser
}: TemplatesPageProps) => {
  const { organizationId } = useParams();
  const [newTemplatesForDeletion, setNewTemplatesForDeletion] = useState<DeleteTemplateRequest[]>([]);
  const [newTemplatesForExport, setNewTemplatesForExport] = useState<Template[]>([]);
  const [selectedTab, setSelectedTab] = useState<ActiveTemplateJobsExplorer>(ActiveTemplateJobsExplorer.ExportCsv);
  const queuedCount = newTemplatesForDeletion.length + newTemplatesForExport.length;
  const [drawerOpen, setDrawerOpen] = useManagementDrawer(queuedCount);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex my-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between shrink-0">
        <h2 className="text-3xl font-bold tracking-tight">Templates</h2>
        {userRole !== UserRole.Viewer && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className="gap-2"
          >
            <Settings2 className="size-4" />
            Manage
            {queuedCount > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5">
                {queuedCount}
              </Badge>
            )}
          </Button>
        )}
      </div>
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        <div className="flex flex-col min-h-0 flex-1 min-w-0">
          <TemplatesTable
            organizationId={organizationId ?? ""}
            activeJobsExplorer={selectedTab}
            newTemplatesForDeletion={newTemplatesForDeletion}
            setNewTemplatesForDeletion={setNewTemplatesForDeletion}
            newTemplatesForExport={newTemplatesForExport}
            setNewTemplatesForExport={setNewTemplatesForExport}
            userRole={userRole}
            isAssignedUser={isAssignedUser}
          />
        </div>
        {userRole !== UserRole.Viewer && drawerOpen && (
          <div className="flex flex-col min-h-0 w-full lg:w-96 shrink-0 rounded-md border bg-background shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <h3 className="font-semibold text-foreground">Manage templates</h3>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
              >
                <X className="size-4" />
              </Button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 p-4">
              <ToggleGroup
                type="single"
                value={String(selectedTab)}
                onValueChange={(v) => { if (v) setSelectedTab(Number(v) as ActiveTemplateJobsExplorer); }}
                variant="outline"
                size="sm"
                className="w-full my-1"
              >
                {(userRole === UserRole.Admin || isAssignedUser) && (
                  <ToggleGroupItem value={String(ActiveTemplateJobsExplorer.DeleteTemplates)} className="flex-1">
                    Delete templates
                  </ToggleGroupItem>
                )}
                <ToggleGroupItem value={String(ActiveTemplateJobsExplorer.ExportCsv)} className="flex-1">
                  Export CSV
                </ToggleGroupItem>
              </ToggleGroup>
              <div className="flex-1 min-h-0 flex flex-col">
                {selectedTab === ActiveTemplateJobsExplorer.DeleteTemplates && (userRole === UserRole.Admin || isAssignedUser) && (
                  <DeleteTemplatesTable
                    organizationId={organizationId ?? ""}
                    newTemplatesForDeletion={newTemplatesForDeletion}
                    setNewTemplatesForDeletion={setNewTemplatesForDeletion}
                  />
                )}
                {selectedTab === ActiveTemplateJobsExplorer.ExportCsv && (
                  <div className="flex-1 overflow-auto min-h-0">
                    <div className="flex flex-col h-full gap-4">
                      {newTemplatesForExport.length > 0 && (
                        <>
                          <div className="flex items-center gap-3 py-2 pt-4">
                            <Separator className="flex-1" />
                            <span className="text-xs font-medium text-muted-foreground"><b>New</b></span>
                            <Separator className="flex-1" />
                          </div>
                          <CreateExportTemplatesToCsvTable
                            organizationId={organizationId ?? ""}
                            newTemplatesForExport={newTemplatesForExport}
                            setNewTemplatesForExport={setNewTemplatesForExport}
                            userRole={userRole}
                            isAssignedUser={isAssignedUser}
                          />
                          <div className="flex items-center gap-3 py-2">
                            <Separator className="flex-1" />
                            <span className="text-xs font-medium text-muted-foreground"><b>Existing exports</b></span>
                            <Separator className="flex-1" />
                          </div>
                        </>
                      )}
                      <ExportTemplatesToCsvStatusTable
                        organizationId={organizationId ?? ""}
                        userRole={userRole}
                        isAssignedUser={isAssignedUser}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatesPage;
